/**
 * Klaris Design System — single source of truth.
 *
 * Every visual decision in the product flows through this file:
 *   - colors (surfaces, borders, text)
 *   - typography ramp
 *   - radius scale
 *   - elevation
 *   - motion (durations, easings)
 *   - sizes (header, sidebar, control heights)
 *
 * If you want to change how the app *looks*, this is the only file to edit.
 * Concrete consumers:
 *   - theme/index.ts            → MUI theme + component defaults
 *   - theme/sidebarTokens.ts    → sidebar colors
 *   - theme/chatTokens.ts       → Copilot chat surfaces
 *   - theme/charts.ts           → chart palette + tooltip / legend / axis
 *
 * Pages and components SHOULD reference design tokens through the theme
 * (e.g. `borderColor: 'border.default'`, `bgcolor: 'surface.raised'`,
 * `borderRadius: 'md'`). Avoid raw rgba(), inline borderRadius numbers,
 * and ad-hoc hex literals — they bypass the system.
 */

import chroma from 'chroma-js';
import type { TenantBranding } from '../hooks/useBranding';
import { brandPalette } from './brandPalette';

// ────────────────────────────────────────────────────────────────────────────
// Type ramp
// ────────────────────────────────────────────────────────────────────────────
// Inter at 14px body, 12px floor (anything smaller is forbidden), generous
// hero. Tracking tightens at large sizes the way Inter wants. Line-heights
// stay relatively high (1.4–1.6) so prose breathes.
//
// All values in rem, computed from a 16px html font-size base.
export const typography = {
  fontFamily: `"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"`,
  fontFamilyMono: `"JetBrains Mono", "SFMono-Regular", "Menlo", "Consolas", monospace`,
  htmlFontSize: 16,

  // Display / hero — used on Copilot home, marketing, empty-state heroes
  displayLg: { fontSize: '3rem',     lineHeight: 1.1, letterSpacing: '-0.025em', fontWeight: 700 }, // 48px
  display:   { fontSize: '2.5rem',   lineHeight: 1.1, letterSpacing: '-0.024em', fontWeight: 700 }, // 40px
  displaySm: { fontSize: '2rem',     lineHeight: 1.15,letterSpacing: '-0.02em',  fontWeight: 700 }, // 32px

  // Headings (page / section / card titles)
  h1: { fontSize: '1.75rem',  lineHeight: 1.2,  letterSpacing: '-0.018em', fontWeight: 700 }, // 28px
  h2: { fontSize: '1.5rem',   lineHeight: 1.25, letterSpacing: '-0.014em', fontWeight: 700 }, // 24px
  h3: { fontSize: '1.25rem',  lineHeight: 1.3,  letterSpacing: '-0.01em',  fontWeight: 700 }, // 20px
  h4: { fontSize: '1.125rem', lineHeight: 1.35, letterSpacing: '-0.005em', fontWeight: 600 }, // 18px
  h5: { fontSize: '1rem',     lineHeight: 1.4,                              fontWeight: 600 }, // 16px
  h6: { fontSize: '0.875rem', lineHeight: 1.45,                             fontWeight: 600 }, // 14px

  // Subtitles
  subtitle1: { fontSize: '0.9375rem', lineHeight: 1.5,                              fontWeight: 600 }, // 15px
  subtitle2: { fontSize: '0.8125rem', lineHeight: 1.5,  letterSpacing: '0.01em',    fontWeight: 600 }, // 13px

  // Body
  bodyLg:    { fontSize: '0.9375rem', lineHeight: 1.6 }, // 15px
  body1:     { fontSize: '0.875rem',  lineHeight: 1.6 }, // 14px ← default
  body2:     { fontSize: '0.8125rem', lineHeight: 1.55 }, // 13px

  // Floor — never go below 12px
  caption:   { fontSize: '0.75rem',   lineHeight: 1.5,                              fontWeight: 500 }, // 12px
  overline:  { fontSize: '0.6875rem', lineHeight: 1.4,  letterSpacing: '0.06em',    fontWeight: 700, textTransform: 'uppercase' as const }, // 11px — only for ALL CAPS labels
  eyebrow:   { fontSize: '0.6875rem', lineHeight: 1.4,  letterSpacing: '0.08em',    fontWeight: 700, textTransform: 'uppercase' as const }, // sidebar section labels

  // Buttons
  button:    { fontSize: '0.875rem',  lineHeight: 1.4,  letterSpacing: '0.005em',   fontWeight: 600, textTransform: 'none' as const },
  buttonSm:  { fontSize: '0.8125rem', lineHeight: 1.4,                              fontWeight: 600, textTransform: 'none' as const },
} as const;

// ────────────────────────────────────────────────────────────────────────────
// Radius scale
// ────────────────────────────────────────────────────────────────────────────
// All component corners must come from this scale. Five steps + full circle.
export const radius = {
  none: 0,
  xs:   4,   // chips, pill-tags, mini stat dots
  sm:   6,   // inputs, buttons, table chrome
  md:   10,  // cards, popovers, menus, dialogs
  lg:   14,  // large cards, dashboards, tall sheets
  xl:   20,  // hero panels, image cards
  full: 9999,
} as const;

// ────────────────────────────────────────────────────────────────────────────
// Spacing — kept on MUI's 8px scale, exposed for explicit references
// ────────────────────────────────────────────────────────────────────────────
export const space = {
  unit: 8,
  px: (n: number) => n * 8,
} as const;

// ────────────────────────────────────────────────────────────────────────────
// Sizes — fixed dimensions that show up across components
// ────────────────────────────────────────────────────────────────────────────
export const sizes = {
  header: 56,            // top app bar
  sidebar: {
    expanded: 248,       // refined from 280 — modern feel
    collapsed: 60,       // refined from 72 — denser icon rail
  },
  control: {
    sm: 28,              // dense buttons / icon buttons
    md: 36,              // primary controls (sidebar items, chips)
    lg: 40,              // large buttons / inputs
  },
} as const;

// ────────────────────────────────────────────────────────────────────────────
// Motion — single, tokenized vocabulary
// ────────────────────────────────────────────────────────────────────────────
export const motion = {
  duration: {
    instant: 80,
    fast:    120,
    normal:  180,
    slow:    260,
    slower:  400,
  },
  easing: {
    standard: 'cubic-bezier(0.2, 0, 0, 1)',
    enter:    'cubic-bezier(0.16, 1, 0.3, 1)',
    exit:     'cubic-bezier(0.4, 0, 1, 1)',
    emphasis: 'cubic-bezier(0.2, 0, 0, 1.2)',
  },
} as const;

// ────────────────────────────────────────────────────────────────────────────
// Color tokens — derived per mode + branding
// ────────────────────────────────────────────────────────────────────────────
// Tokens are *named by role*, not by hue. Use `surface.raised`, never
// "white" or "#fff" directly. This lets dark mode and tenant branding
// flow through automatically.
export type Mode = 'light' | 'dark';

export interface DesignTokens {
  brand: {
    primary: string;          // main brand accent (CTA, active nav, primary chart series)
    primaryHover: string;     // hover state for brand-tinted surfaces
    primaryPressed: string;   // active/pressed state
    primarySoft: string;      // 8–10% tint of primary, for subtle accents
    primarySofter: string;    // 4% tint, for hover surfaces
    onPrimary: string;        // foreground that sits on primary
  };
  surface: {
    canvas: string;           // page background — the deepest layer
    raised: string;           // cards, inputs, dialogs — sits above canvas
    sunken: string;           // grouped sections, code blocks — below canvas
    overlay: string;          // popovers, menus — above raised, with shadow
    inverse: string;          // tooltip, snackbar dark surfaces
  };
  border: {
    subtle: string;           // hairline (0.06 alpha) — most surfaces
    default: string;          // visible (0.10 alpha) — inputs, dividers
    strong: string;           // emphatic (0.18 alpha) — focus rings, strong dividers
    focus: string;            // keyboard focus ring
  };
  text: {
    primary: string;          // body text, titles
    secondary: string;        // subtitles, captions
    tertiary: string;         // metadata, timestamps
    disabled: string;         // disabled controls, placeholders
    inverse: string;          // text on inverse surfaces
    accent: string;           // brand-colored emphasis
  };
  semantic: {
    success: string;
    successSoft: string;
    warning: string;
    warningSoft: string;
    danger: string;
    dangerSoft: string;
    info: string;
    infoSoft: string;
  };
  // Per-component tokens — cleanly grouped, all derived from above.
  sidebar: {
    bg: string;
    border: string;
    sectionLabel: string;     // section eyebrow color
    itemFg: string;
    itemFgMuted: string;
    itemHoverBg: string;
    itemActiveBg: string;
    itemActiveFg: string;
    itemActiveAccent: string; // 2px left rail
    iconRest: string;
    iconActive: string;
  };
  chat: {
    panelBg: string;
    panelBorder: string;
    userBubbleBg: string;
    userBubbleFg: string;
    assistantBubbleBg: string;
    assistantBubbleFg: string;
    codeBg: string;
    chipBg: string;
    chipHoverBg: string;
    chipBorder: string;
    inputBg: string;
    inputBorder: string;
    inputFocusBorder: string;
    messageShadow: string;
    inputShadow: string;
  };
  shadow: {
    none: 'none';
    xs: string;     // hairline lift (cards)
    sm: string;     // resting cards
    md: string;     // popovers, dropdowns
    lg: string;     // dialogs, modals
    xl: string;     // command palette, hero overlays
    focusRing: string;
  };
  chart: {
    palette: string[];        // first entry is always brand.primary
    axis: string;
    axisLine: string;
    grid: string;
    legend: string;
    tooltipBg: string;
    tooltipBorder: string;
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────
const lightAlpha = (a: number) => `rgba(15, 18, 24, ${a})`;
const darkAlpha = (a: number) => `rgba(255, 255, 255, ${a})`;

/**
 * Generate a categorical chart palette anchored to the brand color.
 *
 * Series 0 = brand primary (so the "main" line in every chart is branded).
 * Remaining series rotate evenly around the color wheel using OKLCH for
 * perceptual uniformity (Tableau-like spread without Tableau's exact hues).
 * Saturation/lightness are clamped so colors look at home next to the chrome.
 */
function buildChartPalette(brandHex: string): string[] {
  const base = chroma(brandHex);
  const baseHcl = base.hcl();
  const baseChroma = Math.max(40, Math.min(70, baseHcl[1] || 50));
  const baseLightness = Math.max(50, Math.min(70, baseHcl[2] || 60));

  // Nine evenly-spaced hue rotations starting from the brand hue.
  // Series 0 is the brand color verbatim (so charts stay on-brand).
  const NUM = 10;
  const palette: string[] = [brandHex];
  for (let i = 1; i < NUM; i++) {
    const h = ((baseHcl[0] || 260) + (i * 360) / NUM) % 360;
    // Slightly damp every other series for visual grouping.
    const c = baseChroma * (i % 2 === 0 ? 0.95 : 0.85);
    const l = baseLightness + (i % 3 === 0 ? -6 : i % 3 === 1 ? 0 : 6);
    palette.push(chroma.hcl(h, c, l).hex());
  }
  return palette;
}

// ────────────────────────────────────────────────────────────────────────────
// Token factory — call once per render with the current mode and branding.
// ────────────────────────────────────────────────────────────────────────────
export function buildTokens(mode: Mode, branding?: TenantBranding | null): DesignTokens {
  const isLight = mode === 'light';
  const a = isLight ? lightAlpha : darkAlpha;
  const inverseA = isLight ? darkAlpha : lightAlpha;

  // Brand — tenant overrides win. Only the main brand colour (shade 400)
  // is consumed; hover/pressed/soft variants are all chroma-derived so we
  // never depend on tenants supplying a full shade scale.
  const brandShades = branding?.colors?.primary || brandPalette.primary;
  const primary = brandShades['400'];
  const primaryDark = chroma(primary).darken(0.7).hex();

  // Semantic — tenant overrides for success/warning/error if present.
  const sem = branding?.colors?.semantic || {
    success: '#16a34a',
    warning: '#d97706',
    error: brandPalette.semantic.red[200],
  };
  const info = primary;

  // ── Brand tokens
  const brand = {
    primary,
    primaryHover: primaryDark,
    primaryPressed: primaryDark,
    primarySoft: chroma(primary).alpha(isLight ? 0.10 : 0.16).css(),
    primarySofter: chroma(primary).alpha(isLight ? 0.06 : 0.10).css(),
    onPrimary: '#FFFFFF',
  };

  // ── Surfaces
  // Light mode uses a near-neutral cool gray canvas (slightly warmer than pure
  // white) so that white "raised" cards visibly lift. Dark mode uses a deep
  // blue-black canvas with a slightly lighter raised tone.
  const surface = {
    canvas: isLight ? '#FAFAFB' : '#0B0E14',
    raised: isLight ? '#FFFFFF' : '#13161D',
    sunken: isLight ? '#F4F4F7' : '#0F1218',
    overlay: isLight ? '#FFFFFF' : '#181C25',
    inverse: isLight ? '#101115' : '#FFFFFF',
  };

  // ── Borders
  const border = {
    subtle: a(0.06),
    default: a(0.10),
    strong: a(0.18),
    focus: chroma(primary).alpha(0.45).css(),
  };

  // ── Text
  const text = {
    primary: isLight ? '#101115' : '#E7ECF5',
    secondary: isLight ? '#5C6370' : '#A4ABB8',
    tertiary: isLight ? '#8A91A0' : '#7A8294',
    disabled: isLight ? '#B0B6C2' : '#5F6675',
    inverse: isLight ? '#FFFFFF' : '#101115',
    accent: primary,
  };

  // ── Sidebar — canvas-color so content cards "lift" out of it (Linear/Notion).
  const sidebar = {
    bg: surface.canvas,
    border: border.subtle,
    sectionLabel: text.tertiary,
    itemFg: text.secondary,
    itemFgMuted: text.tertiary,
    itemHoverBg: a(isLight ? 0.04 : 0.06),
    itemActiveBg: brand.primarySoft,
    itemActiveFg: text.accent,
    itemActiveAccent: primary,
    iconRest: text.tertiary,
    iconActive: primary,
  };

  // ── Chat — derived from primary color and surface tokens
  const chat = {
    panelBg: surface.raised,
    panelBorder: border.subtle,
    userBubbleBg: chroma(primary).alpha(isLight ? 0.10 : 0.16).css(),
    userBubbleFg: text.primary,
    assistantBubbleBg: isLight ? surface.sunken : a(0.04),
    assistantBubbleFg: text.primary,
    codeBg: isLight ? '#F5F6F7' : a(0.06),
    chipBg: 'transparent',
    chipHoverBg: brand.primarySofter,
    chipBorder: border.default,
    inputBg: surface.raised,
    inputBorder: border.default,
    inputFocusBorder: chroma(primary).alpha(0.6).css(),
    messageShadow: isLight ? '0 1px 2px rgba(15,23,42,0.04)' : '0 1px 2px rgba(0,0,0,0.2)',
    inputShadow: isLight ? '0 1px 2px rgba(15,23,42,0.04)' : '0 1px 2px rgba(0,0,0,0.25)',
  };

  // ── Elevation — cool-tinted shadows in light mode (more refined than pure black)
  const shadow = isLight
    ? {
        none: 'none' as const,
        xs: '0 1px 2px rgba(15,23,42,0.05), 0 1px 1px rgba(15,23,42,0.04)',
        sm: '0 2px 4px rgba(15,23,42,0.06), 0 1px 2px rgba(15,23,42,0.04)',
        md: '0 6px 16px rgba(15,23,42,0.08), 0 2px 4px rgba(15,23,42,0.04)',
        lg: '0 16px 32px rgba(15,23,42,0.10), 0 4px 8px rgba(15,23,42,0.06)',
        xl: '0 24px 48px rgba(15,23,42,0.12), 0 8px 16px rgba(15,23,42,0.08)',
        focusRing: `0 0 0 3px ${chroma(primary).alpha(0.18).css()}`,
      }
    : {
        none: 'none' as const,
        xs: '0 1px 2px rgba(0,0,0,0.30)',
        sm: '0 2px 6px rgba(0,0,0,0.35), 0 1px 2px rgba(0,0,0,0.24)',
        md: '0 8px 20px rgba(0,0,0,0.40), 0 2px 6px rgba(0,0,0,0.28)',
        lg: '0 18px 36px rgba(0,0,0,0.50), 0 4px 12px rgba(0,0,0,0.32)',
        xl: '0 28px 56px rgba(0,0,0,0.55), 0 8px 18px rgba(0,0,0,0.40)',
        focusRing: `0 0 0 3px ${chroma(primary).alpha(0.32).css()}`,
      };

  // ── Charts — first series = brand color, derived rotations follow.
  const customCharts = branding?.colors?.charts;
  const palette = customCharts && customCharts.length > 0
    ? customCharts
    : buildChartPalette(primary);

  const chart = {
    palette,
    axis: isLight ? '#64748B' : '#94A3B8',
    axisLine: isLight ? '#CBD5E1' : '#334155',
    grid: isLight ? '#E2E8F0' : '#1E293B',
    legend: isLight ? '#334155' : '#CBD5E1',
    tooltipBg: isLight ? 'rgba(255,255,255,0.97)' : 'rgba(24,28,37,0.97)',
    tooltipBorder: isLight ? '#E2E8F0' : '#1E293B',
  };

  return {
    brand,
    surface,
    border,
    text,
    semantic: {
      success: sem.success,
      successSoft: chroma(sem.success).alpha(0.12).css(),
      warning: sem.warning,
      warningSoft: chroma(sem.warning).alpha(0.14).css(),
      danger: sem.error,
      dangerSoft: chroma(sem.error).alpha(0.12).css(),
      info,
      infoSoft: brand.primarySoft,
    },
    sidebar,
    chat,
    shadow,
    chart,
  };
  // Note: `inverseA` defined above is intentionally unused here; reserved for
  // future inverse-on-surface tokens (e.g. dark tooltips on light pages).
  // Suppress unused-var warning by no-op:
  void inverseA;
}

// Convenience — token snapshot for non-React contexts that just need the
// brand fallback (rare; prefer reading from theme).
export const FALLBACK_TOKENS = buildTokens('light', null);
